import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { QuoteTotalStrip } from '@/components/QuoteTotalStrip';
import { MakeSeparateQuoteButton } from '@/components/MakeSeparateQuoteButton';
import SaveDocButton from './SaveDocButton';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Star, Loader2, CheckCircle2, RefreshCw, Copy, Printer, MapPin, Edit3, X, Calculator, FileText, AlertTriangle } from 'lucide-react';
import RoofDiagramAnnotator from './RoofDiagramAnnotator';
import { RoofM2ConfidenceBanner } from './RoofM2ConfidenceBanner';
import { CalcShadowProvider, CalcShadowToggle, CalcShadow } from '@/components/CalcShadow';
import CompareQuotesDialog from '@/components/CompareQuotesDialog';
import BuilderRoofDetailsPanel from '@/components/BuilderRoofDetailsPanel';
import ComponentsFlashingEmbed from '@/components/shared/ComponentRow/ComponentsFlashingEmbed';

/**
 * Re-roof Builder Tab — guided Iron-quote spec engine.
 *
 * Operator workflow (matches the spec from the salesperson):
 *   1. Add one or more "specs" (variants) per appraisal. Most jobs need
 *      just ONE — the client knows what they want. Optional: clone a spec
 *      to compare Galv vs Zinc vs Colorsteel or different profiles.
 *   2. Each spec auto-pre-fills roof_m² / perimeter / ridge / valley from
 *      the diagram annotations the office already drew. Operator can
 *      override any field.
 *   3. Live cost breakdown appears beside the form — purlin BOM (4.8 m +
 *      6 m stock optimised), TekScrew count (varies by profile),
 *      underlay m², labour. Updates as fields change.
 *   4. ★ Mark one spec as the headline ("chosen") quote — it's the one
 *      that pushes to Components for the final tradespack / quote PDF.
 *   5. Tick "Include in summary" on any alternate specs the customer
 *      wants priced — they'll appear in a comparison strip at the bottom
 *      of the quote PDF (without each generating a full quote).
 *   6. "Push to Components" creates `component_lines` tagged with this
 *      spec's id so re-pushing is idempotent.
 */
export default function ReroofBuilderTab({ api, appraisalId, refreshKey = 0 }) {
  const [specs, setSpecs] = useState([]);
  const [dims, setDims] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [flash, setFlash] = useState(null);
  const [pushing, setPushing] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  // Bumped after every Live-Dims sync so the SpecEditor re-mounts and
  // re-reads form fields from the freshly-PUT spec. Without this, the
  // editor only re-seeds on spec._id change (line in SpecEditor) and the
  // operator sees stale dim numbers + unchanged pricing after a sync.
  const [dimsSyncTick, setDimsSyncTick] = useState(0);
  // Toggle: when on, the Foreman Bundle PDF inserts a side-by-side
  // variant comparison table (Galv vs Zinc vs Maxx etc.) on the cover
  // page so the client can pick at the kitchen table.
  const [bundleCompare, setBundleCompare] = useState(false);

  // v10.29 Sprint 4a (2026-02c) — Yellow re-push banner + per-appraisal
  // dismiss (localStorage). Reminds the operator that re-pushing this
  // variant will overwrite any manual edits to `reroof_builder` tagged
  // rows in Components tab. Bespoke Extras (Slice D) + Misc Hours
  // (Slice H) survive Push because they use sticky import_source tags.
  // Dismiss is scoped per-appraisal so dismissing on job A doesn't hide
  // the reminder on job B.
  const bannerKey = `reroof_banner_dismissed_${appraisalId}`;
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return typeof localStorage !== 'undefined' && localStorage.getItem(bannerKey) === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try {
      setBannerDismissed(typeof localStorage !== 'undefined'
                         && localStorage.getItem(bannerKey) === '1');
    } catch { /* ignore quota / privacy */ }
  }, [bannerKey]);
  const dismissBanner = useCallback(() => {
    try { localStorage.setItem(bannerKey, '1'); } catch { /* ignore */ }
    setBannerDismissed(true);
  }, [bannerKey]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [defaultsRes, dimsRes, specsRes] = await Promise.all([
        api.get('/reroof-builder/defaults'),
        api.get(`/appraisals/${appraisalId}/reroof-builder/dims`),
        api.get(`/appraisals/${appraisalId}/reroof-specs`),
      ]);
      setDefaults(defaultsRes.data || null);
      setDims(dimsRes.data || null);
      const list = Array.isArray(specsRes.data) ? specsRes.data : [];
      setSpecs(list);
      if (list.length && !activeId) setActiveId(list[0]._id);
    } catch (e) {
      setFlash({ kind: 'err', text: e?.response?.data?.detail || 'Failed to load re-roof builder' });
    } finally {
      setLoading(false);
    }
  }, [api, appraisalId, activeId]);

  // v10.18 (Feb 2026 · UAT A999577) · `refreshKey` in deps so the
  // parent AppraisalDetailPage can force a full dims + specs reload
  // when the Master Diagram popout closes. Without this the operator
  // had to hit "Sync all from live dims" manually every time they
  // edited pins on the annotator.
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [appraisalId, refreshKey]);

  const activeSpec = useMemo(
    () => specs.find((s) => s._id === activeId) || null,
    [specs, activeId]
  );

  const handleAddSpec = useCallback(async () => {
    try {
      // Pre-seed with the diagram dims so the operator only re-types
      // what they want to change.
      const seed = {
        name: `Option ${String.fromCharCode(65 + specs.length)}`,
        profile: defaults?.profiles?.[0]?.key || 'Corrugate',
        material_kind: 'Maxam',   // R2 2026-02 · Maxam is the current default (was Colorsteel)
        gauge: '0.40',            // R2 · default gauge sub-variant
        framing_type: 'Standard rafters 600 ctrs · 900 purlins',
        underlay_type: '215 Covertek',   // R2 · Covertek 215 (50 m²/roll) is the new default
        off_cut_pct: 8,
        roof_m2: dims?.roof_m2 || 0,
        perimeter_m: dims?.perimeter_m || 0,
        ridge_total_m: dims?.ridge_total_m || 0,
        valley_total_m: dims?.valley_total_m || 0,
      };
      const { data } = await api.post(`/appraisals/${appraisalId}/reroof-specs`, seed);
      await loadAll();
      setActiveId(data._id);
      setFlash({ kind: 'ok', text: `Created ${data.name}` });
      setTimeout(() => setFlash(null), 2500);
    } catch (e) {
      setFlash({ kind: 'err', text: e?.response?.data?.detail || 'Failed to create spec' });
    }
  }, [api, appraisalId, specs.length, defaults, dims, loadAll]);

  const handleCloneSpec = useCallback(async (spec, overrides = {}) => {
    // Bob's Test R3 (2026-02) · Bug 4 — variant uniqueness. One
    // {material_kind, gauge} combo per parent appraisal. If the target
    // combo already exists as another spec, JUMP to it instead of
    // creating a duplicate (Q2b answer — "allow the click with a note").
    const targetMaterial = overrides.material_kind || spec.material_kind;
    const targetGauge    = overrides.gauge || spec.gauge || '0.40';
    const existing = specs.find((s) => (
      s._id !== spec._id
      && (s.material_kind || '') === targetMaterial
      && (s.gauge || '0.40') === targetGauge
    ));
    if (existing) {
      setActiveId(existing._id);
      setFlash({
        kind: 'info',
        text: `${targetMaterial} ${targetGauge} already exists as "${existing.name}" — jumping to it.`,
      });
      setTimeout(() => setFlash(null), 3500);
      return;
    }
    // Default name pattern: when overriding material_kind, label the
    // copy after the new material so the spec list reads cleanly.
    const matLabel = overrides.material_kind && overrides.material_kind !== spec.material_kind
      ? overrides.material_kind
      : null;
    const baseName = matLabel
      ? `${spec.name.replace(/\s*\((copy|Galv|Zinc|Maxam|Zincacore|Colorsteel|Maxx|Zcore)\)\s*$/i, '')} (${matLabel})`
      : (overrides.gauge ? `${targetMaterial} ${targetGauge}` : `${spec.name} (copy)`);
    const seed = {
      ...spec,
      ...overrides,
      name: baseName,
      is_chosen: false,
    };
    delete seed._id;
    delete seed.created_at;
    delete seed.updated_at;
    delete seed.calc_snapshot;
    const { data } = await api.post(`/appraisals/${appraisalId}/reroof-specs`, seed);
    await loadAll();
    setActiveId(data._id);
  }, [api, appraisalId, loadAll, specs, setFlash]);

  const handleDeleteSpec = useCallback(async (spec) => {
    if (!window.confirm(`Delete "${spec.name}"? Any component lines pushed by this spec will be removed too.`)) return;
    await api.delete(`/appraisals/${appraisalId}/reroof-specs/${spec._id}`);
    await loadAll();
    if (activeId === spec._id) setActiveId(null);
  }, [api, appraisalId, activeId, loadAll]);

  const handleSaveField = useCallback(async (spec, patch) => {
    const { data } = await api.put(
      `/appraisals/${appraisalId}/reroof-specs/${spec._id}`, patch
    );
    setSpecs((prev) => prev.map((s) => (s._id === spec._id ? data : s)));
    // If is_chosen got toggled, demote siblings locally too
    if (patch.is_chosen) {
      setSpecs((prev) => prev.map((s) => s._id === spec._id ? data : { ...s, is_chosen: false }));
    }
  }, [api, appraisalId]);

  // Push the current Live Dimensions snapshot (roof m², perimeter, ridge,
  // valley, spouting, barges, pipes, flues) into a single spec. Only
  // writes fields where the dim value is non-empty so the operator's
  // intentional overrides for fields we can't auto-source (e.g.
  // screws_per_m2) are preserved. Returns the map of fields that
  // actually changed so the caller can flash a useful summary.
  const buildDimsPatch = useCallback((spec) => {
    if (!dims) return null;
    const fCandidates = {
      roof_m2: dims.roof_m2 != null ? Number(dims.roof_m2) : null,
      perimeter_m: dims.perimeter_m != null ? Number(dims.perimeter_m) : null,
      ridge_total_m: dims.ridge_total_m != null ? Number(dims.ridge_total_m) : null,
      valley_total_m: dims.valley_total_m != null ? Number(dims.valley_total_m) : null,
      spouting_total_m: dims.spouting_total_m != null ? Number(dims.spouting_total_m) : null,
      barge_total_m: dims.barge_total_m != null ? Number(dims.barge_total_m) : null,
      pipes_total_m: dims.pipes_total_m != null ? Number(dims.pipes_total_m) : null,
    };
    const iCandidates = {
      pipes_count: dims.pipes_count != null ? Number(dims.pipes_count) : null,
      flues_count: dims.flues_count != null ? Number(dims.flues_count) : null,
    };
    const patch = {};
    Object.entries(fCandidates).forEach(([k, v]) => {
      if (v == null || !isFinite(v)) return;
      const curr = Number(spec?.[k] || 0);
      if (Math.abs(curr - v) > 0.005) patch[k] = v;
    });
    Object.entries(iCandidates).forEach(([k, v]) => {
      if (v == null || !isFinite(v)) return;
      const curr = Number(spec?.[k] || 0);
      if (curr !== v) patch[k] = v;
    });
    return patch;
  }, [dims]);

  const handleSyncDimsOne = useCallback(async (spec) => {
    const patch = buildDimsPatch(spec);
    if (!patch || !Object.keys(patch).length) {
      setFlash({ kind: 'ok', text: `${spec.name} already matches Live Dimensions — nothing to update.` });
      setTimeout(() => setFlash(null), 3000);
      return;
    }
    try {
      const { data } = await api.put(
        `/appraisals/${appraisalId}/reroof-specs/${spec._id}`, patch
      );
      setSpecs((prev) => prev.map((s) => (s._id === spec._id ? data : s)));
      setDimsSyncTick((t) => t + 1);
      const fields = Object.keys(patch).map((k) => k.replace(/_m2$/, ' m²').replace(/_total_m$/, ' m').replace(/_m$/, ' m')).join(', ');
      setFlash({ kind: 'ok', text: `Synced ${spec.name} from Live Dimensions (${fields})` });
      setTimeout(() => setFlash(null), 4000);
    } catch (e) {
      setFlash({ kind: 'err', text: e?.response?.data?.detail || `Failed to sync ${spec.name}` });
    }
  }, [api, appraisalId, buildDimsPatch]);

  const handleSyncDimsAll = useCallback(async () => {
    if (!specs.length) return;
    if (!dims) {
      setFlash({ kind: 'err', text: 'Live Dimensions not loaded yet — try refreshing the card.' });
      return;
    }
    const targets = specs.map((s) => ({ spec: s, patch: buildDimsPatch(s) }))
                         .filter(t => t.patch && Object.keys(t.patch).length);
    if (!targets.length) {
      setFlash({ kind: 'ok', text: 'All variants already match Live Dimensions — nothing to update.' });
      setTimeout(() => setFlash(null), 3000);
      return;
    }
    if (!window.confirm(
      `Update ${targets.length} variant${targets.length === 1 ? '' : 's'} with the current Live Dimensions (roof m², perimeter, ridge, valley, spouting, barges, pipes, flues)?\n\nIndividual screws/m², profile and material choices are not touched.`
    )) return;
    try {
      const updated = await Promise.all(targets.map(async (t) => {
        const { data } = await api.put(
          `/appraisals/${appraisalId}/reroof-specs/${t.spec._id}`, t.patch
        );
        return data;
      }));
      const updatedById = new Map(updated.map(d => [d._id, d]));
      setSpecs((prev) => prev.map((s) => updatedById.get(s._id) || s));
      setDimsSyncTick((t) => t + 1);
      setFlash({ kind: 'ok', text: `Synced ${updated.length} variant${updated.length === 1 ? '' : 's'} from Live Dimensions` });
      setTimeout(() => setFlash(null), 4000);
    } catch (e) {
      setFlash({ kind: 'err', text: e?.response?.data?.detail || 'Bulk sync failed' });
    }
  }, [api, appraisalId, specs, dims, buildDimsPatch]);

  const handlePush = useCallback(async (spec) => {
    setPushing(spec._id);
    try {
      const { data } = await api.post(
        `/appraisals/${appraisalId}/reroof-specs/${spec._id}/push-to-components`
      );
      setFlash({
        kind: 'ok',
        text: `Pushed ${data.lines_created} line(s) to Components · Grand $${(data.grand_total || 0).toLocaleString()}`,
      });
      setTimeout(() => setFlash(null), 5000);
      await loadAll();
    } catch (e) {
      setFlash({ kind: 'err', text: e?.response?.data?.detail || 'Push failed' });
    } finally {
      setPushing(null);
    }
  }, [api, appraisalId, loadAll]);

  const handlePrintSpec = useCallback(async (spec) => {
    // Stream the per-spec PDF in a new tab. Includes the side-by-side
    // comparison page when there are 2+ specs flagged include_in_summary.
    try {
      const includeOptions = specs.filter(s => s.include_in_summary).length >= 2 ? 1 : 0;
      const { data } = await api.get(
        `/appraisals/${appraisalId}/reroof-specs/${spec._id}/spec-sheet.pdf`,
        { responseType: 'blob', params: { include_options: includeOptions } }
      );
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setFlash({ kind: 'err', text: e?.response?.data?.detail || 'Failed to render spec sheet PDF' });
    }
  }, [api, appraisalId, specs]);

  const handlePrintBundle = useCallback(async (includeUnchosen = false, compareVariants = false) => {
    // Foreman Bundle PDF — cover page + every chosen / summary spec
    // sheet concatenated into one document for the on-site team.
    try {
      const { data } = await api.get(
        `/appraisals/${appraisalId}/foreman-bundle.pdf`,
        { responseType: 'blob', params: {
            include_unchosen: includeUnchosen ? 1 : 0,
            compare: compareVariants ? 1 : 0,
          } }
      );
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      let detail = 'Failed to generate foreman bundle';
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          if (j?.detail) detail = j.detail;
        } else if (e?.response?.data?.detail) {
          detail = e.response.data.detail;
        }
      } catch (_) {/* ignore */}
      setFlash({ kind: 'err', text: detail });
    }
  }, [api, appraisalId]);

  if (loading && !defaults) {
    return (
      <div className="p-6 text-center text-slate-500" data-testid="reroof-builder-loading">
        <Loader2 size={20} className="inline animate-spin mr-2" /> Loading re-roof builder…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="reroof-builder-tab">
      <div className="flex justify-end">
        <SaveDocButton
          appraisalId={appraisalId}
          docKind="quotation_detailed"
          label="Save Quote PDF"
          expandChildren={false}
          variant="outline"
          size="sm"
          testId="reroof-save-quote-pdf"
        />
      </div>
      {flash && (
        <div
          className={`px-3 py-2 rounded text-sm flex items-center gap-2 ${
            flash.kind === 'ok'
              ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
              : 'bg-rose-50 text-rose-800 ring-1 ring-rose-200'
          }`}
          data-testid="reroof-builder-flash"
        >
          {flash.kind === 'ok' && <CheckCircle2 size={14} />} {flash.text}
          <button className="ml-auto text-xs underline" onClick={() => setFlash(null)}>dismiss</button>
        </div>
      )}

      {/* v10.29 Sprint 4a (2026-02c) · Slice A · Re-push overwrite
          reminder. Dismissible per-appraisal (localStorage). Kept
          concise + amber so it reads as informational, not alarming. */}
      {!bannerDismissed && (
        <div className="px-3 py-2 rounded text-xs flex items-start gap-2 bg-amber-50 text-amber-900 ring-1 ring-amber-200"
             data-testid="reroof-repush-banner">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <strong>Heads-up:</strong> Re-pushing a variant overwrites any manual edits made to
            builder-emitted lines in the <span className="font-semibold">Components</span> tab.
            Bespoke <em>Extras</em> and <em>Misc Hours</em> added on the builder are preserved.
          </div>
          <button className="text-[11px] underline hover:no-underline"
                  onClick={dismissBanner}
                  data-testid="reroof-repush-banner-dismiss">
            dismiss (this appraisal)
          </button>
        </div>
      )}

      <DimensionsCard dims={dims} onRefresh={loadAll} />

      {/* Iter167 — Roof Details quick-edit panel under the Re-roof
          banner. Reuses the polished RoofDetailsQuickBar (⚙ Fields
          picker + ✎ Edit full + Apply/Discard) via a shared wrapper. */}
      <BuilderRoofDetailsPanel
        api={api}
        appraisalId={appraisalId}
        onApplied={loadAll}
      />

      {/* Embedded annotated roof diagram — read-only thumbnail with
          an "Open editor" toggle that pops the full editor in a modal
          so the operator can verify takeoff measurements without
          leaving this tab. */}
      <EmbeddedRoofDiagram
        api={api}
        appraisalId={appraisalId}
        onChanged={loadAll}
      />

      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-lg font-bold text-[#0F172A]">Quote variants</h2>
        <span className="text-xs text-slate-500">{specs.length} spec{specs.length === 1 ? '' : 's'}</span>
        <button onClick={() => setShowCompare(true)} data-testid="reroof-compare-btn"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-xs font-semibold rounded-md ring-1 ring-emerald-300">
          <FileText size={12} /> Compare Quotes
        </button>
        <CompareQuotesDialog api={api} appraisalId={appraisalId}
                              open={showCompare} onOpenChange={setShowCompare} />
        {specs.length > 0 && (
          <button
            data-testid="reroof-sync-dims-all-btn"
            onClick={handleSyncDimsAll}
            disabled={!dims}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white hover:bg-sky-50 text-sky-800 text-xs font-semibold rounded-md ring-1 ring-sky-300 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Update every variant's roof m² / perimeter / ridge / valley from the current Live Dimensions card. Material, profile and screws/m² are NOT touched."
          >
            <RefreshCw size={12} /> Sync all from live dims
          </button>
        )}
        {specs.length > 0 && (
          <>
            {specs.length >= 2 && (
              <label
                className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-600 select-none cursor-pointer px-2 py-1.5 rounded-md ring-1 ring-slate-200 bg-white hover:bg-slate-50"
                title="When on, the Foreman Bundle PDF inserts a side-by-side variant comparison table (totals + $/m²) on the cover page"
                data-testid="reroof-bundle-compare-toggle-label"
              >
                <input
                  type="checkbox"
                  checked={bundleCompare}
                  onChange={(e) => setBundleCompare(e.target.checked)}
                  className="accent-orange-600"
                  data-testid="reroof-bundle-compare-toggle"
                />
                Compare variants on cover
              </label>
            )}
            <button
              data-testid="reroof-foreman-bundle-btn"
              onClick={() => handlePrintBundle(false, bundleCompare)}
              className={`${specs.length >= 2 ? '' : 'ml-auto'} inline-flex items-center gap-2 px-3 py-2 bg-white hover:bg-orange-50 text-orange-800 text-sm font-semibold rounded-md shadow-sm ring-1 ring-orange-300`}
              title={bundleCompare
                ? "Print the on-site Foreman Bundle PDF with a side-by-side variant comparison table on the cover"
                : "Print the on-site Foreman Bundle PDF — cover page + chosen + in-summary spec sheets in one print run"}
            >
              <Printer size={14} /> Foreman Bundle PDF{bundleCompare ? ' (compare)' : ''}
            </button>
          </>
        )}
        <button
          data-testid="reroof-add-spec-btn"
          onClick={handleAddSpec}
          className={`${specs.length > 0 ? '' : 'ml-auto'} inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-md shadow-sm`}
        >
          <Plus size={15} /> Add spec
        </button>
      </div>

      {!specs.length && (
        <div className="p-6 bg-white rounded ring-1 ring-slate-200 text-center" data-testid="reroof-empty-state">
          <p className="text-slate-600">No re-roof specs yet on this appraisal.</p>
          <p className="text-xs text-slate-500 mt-1">
            Click <span className="font-semibold">Add spec</span> to start building. The form auto-fills from the diagram dimensions above.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {specs.length > 0 && (
          <div className="space-y-2" data-testid="reroof-spec-list">
            {specs.map((s) => (
              <SpecListCard
                key={s._id}
                spec={s}
                active={s._id === activeId}
                onSelect={() => setActiveId(s._id)}
                onToggleChosen={() => handleSaveField(s, { is_chosen: !s.is_chosen })}
                onToggleSummary={() => handleSaveField(s, { include_in_summary: !s.include_in_summary })}
                onClone={() => handleCloneSpec(s)}
                onDelete={() => handleDeleteSpec(s)}
                onPrint={() => handlePrintSpec(s)}
                onSyncDims={() => handleSyncDimsOne(s)}
                dimsAvailable={!!dims}
              />
            ))}
          </div>
        )}
        {activeSpec && defaults && (
          <SpecEditor
            key={`${activeSpec._id}:${dimsSyncTick}`}
            spec={activeSpec}
            defaults={defaults}
            dims={dims}
            api={api}
            appraisalId={appraisalId}
            pushing={pushing === activeSpec._id}
            onSaveField={(patch) => handleSaveField(activeSpec, patch)}
            onPush={() => handlePush(activeSpec)}
            onCloneAsMaterial={(material_kind) => handleCloneSpec(activeSpec, { material_kind })}
            onCloneAsGauge={(gauge) => handleCloneSpec(activeSpec, { gauge })}
            // Bob's Test R3 · Bug 4 — set of existing {material, gauge}
            // combos so the SpecEditor can colour clone-variant buttons
            // that would create a duplicate.
            existingCombos={new Set(specs.map((s) => `${s.material_kind || ''}::${s.gauge || '0.40'}`))}
            currentGauge={activeSpec.gauge || '0.40'}
          />
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
function DimensionsCard({ dims, onRefresh }) {
  if (!dims) return null;
  // Ridging = Ridge + Hip combined (they're the same cap stock).
  const ridging = (dims.ridging_total_m != null)
    ? dims.ridging_total_m
    : (Number(dims.ridge_total_m || 0) + Number(dims.hip_total_m || 0));
  const pipesLabel = (dims.pipes_count || 0) > 0
    ? `${(dims.pipes_total_m || 0).toFixed(1)} m · ${dims.pipes_count}× pipes${(dims.pipes_diameters_mm || []).length ? ` (${dims.pipes_diameters_mm.join('/')} mm)` : ''}`
    : '—';
  const fluesLabel = (dims.flues_count || 0) > 0
    ? `${dims.flues_count}× flues${(dims.flues_diameters_mm || []).length ? ` (${dims.flues_diameters_mm.join('/')} mm)` : ''}`
    : '—';
  // R6-6 (Mike Test 2026-02-A6396) — surface the flashing family
  // breakdown that used to hide inside the diagram Pin-Totals panel
  // only. Format: `6.6 m · 3× flashings (2 Backtray · 1 Apron)`.
  const flashingsLabel = (dims.flashings_count || 0) > 0
    ? `${(dims.flashings_total_m || 0).toFixed(1)} m · ${dims.flashings_count}× flashings${(() => {
        const fam = dims.flashings_by_family || {};
        const parts = Object.entries(fam).map(([k, v]) => `${v.count} ${k}`);
        return parts.length ? ` (${parts.join(' · ')})` : '';
      })()}`
    : '—';
  const items = [
    { k: 'Roof m²',  v: dims.roof_m2,            suffix: ' m²' },
    { k: 'Perimeter', v: dims.perimeter_m,       suffix: ' m'  },
    { k: 'Spouting', v: dims.spouting_total_m,   suffix: ' m'  },
    { k: 'Barges',   v: dims.barge_total_m,      suffix: ' m'  },
    { k: 'Ridging',  v: ridging,                 suffix: ' m', sub: (() => {
        // Bob's Test R4 (2026-02) · R4-Bug-2 — show the ridge_role
        // breakdown so operators can spot mistyped pins at a glance.
        // Falls back to legacy ridge/hip counts if breakdown missing.
        const bd = dims.ridging_role_breakdown;
        if (bd) {
          return `main ${bd.main_count || 0} × total ${(bd.main_total_m || 0).toFixed(1)}m · hip ${bd.hip_count || 0} × total ${(bd.hip_total_m || 0).toFixed(1)}m`;
        }
        return `ridge ${(dims.ridge_total_m||0).toFixed(1)} + hip ${(dims.hip_total_m||0).toFixed(1)}`;
      })() },
    { k: 'Valley',   v: dims.valley_total_m,     suffix: ' m'  },
    { k: 'Flashings', text: flashingsLabel, testid: 'reroof-dims-flashings' },
    { k: 'Pipes',    text: pipesLabel },
    { k: 'Flues',    text: fluesLabel },
  ];

  // Freshness: compare the diagram's saved-at to the dims computed-at.
  // If the diagram is newer by more than the small clock-skew margin
  // (1 s), the snapshot is stale and the operator should click refresh.
  const computedAt = dims.dims_computed_at ? new Date(dims.dims_computed_at) : null;
  const diagramAt  = dims.diagram_updated_at ? new Date(dims.diagram_updated_at) : null;
  const stale = computedAt && diagramAt && (diagramAt.getTime() - computedAt.getTime() > 1000);
  const fmt = (d) => d ? d.toLocaleString('en-NZ', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  return (
    <div
      className={`p-3 rounded ring-1 ${stale ? 'bg-amber-50 ring-amber-300' : 'bg-slate-50 ring-slate-200'}`}
      data-testid="reroof-dims-card"
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h3 className="text-sm font-bold text-slate-700">
          Live dimensions <span className="text-[10px] uppercase tracking-wider text-slate-500 align-middle">(totals)</span> from roof diagram {dims.annotations_present ? '✓' : '· empty — using zeros'}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500" title={`Snapshot taken ${fmt(computedAt)}`} data-testid="reroof-dims-computed-at">
            snapshot · {fmt(computedAt)}
          </span>
          <button
            onClick={onRefresh}
            className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
            data-testid="reroof-dims-refresh"
          >
            <RefreshCw size={12} /> refresh
          </button>
        </div>
      </div>
      {stale && (
        <div
          data-testid="reroof-dims-stale-banner"
          className="mb-2 text-xs text-amber-900 bg-amber-100 border border-amber-300 rounded px-2 py-1.5 flex items-center justify-between gap-2"
        >
          <span>
            ⚠ Diagram was edited after this snapshot (drawing saved {fmt(diagramAt)}). The values below may be out of date.
          </span>
          <button
            onClick={onRefresh}
            className="text-xs font-bold text-amber-900 underline hover:text-amber-700"
            data-testid="reroof-dims-stale-refresh"
          >
            Refresh now
          </button>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        {items.map(({ k, v, suffix, sub, text }) => (
          <div key={k} className="bg-white rounded px-2 py-1.5 ring-1 ring-slate-200" data-testid={`reroof-dims-${k.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
            <div className="text-sm font-bold text-[#0F172A]">
              {text != null ? text : `${(v != null && v.toFixed) ? v.toFixed(2) : 0}${suffix || ''}`}
            </div>
            {sub && <div className="text-[9px] italic text-slate-400 mt-0.5">{sub}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
function SpecListCard({ spec, active, onSelect, onToggleChosen, onToggleSummary, onClone, onDelete, onPrint, onSyncDims, dimsAvailable }) {
  const snap = spec.calc_snapshot || {};
  return (
    <div
      className={`p-3 rounded ring-1 cursor-pointer transition-all ${
        active ? 'ring-[#D35400] bg-orange-50' : 'ring-slate-200 bg-white hover:ring-slate-300'
      }`}
      onClick={onSelect}
      data-testid={`reroof-spec-card-${spec._id}`}
    >
      <div className="flex items-start gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); onToggleChosen(); }}
          className={`mt-0.5 ${spec.is_chosen ? 'text-amber-500' : 'text-slate-300 hover:text-amber-400'}`}
          title={spec.is_chosen ? 'Chosen headline quote' : 'Mark as chosen'}
          data-testid="reroof-spec-chosen-toggle"
        >
          <Star size={16} fill={spec.is_chosen ? 'currentColor' : 'none'} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-[#0F172A] truncate">{spec.name}</div>
          <div className="text-xs text-slate-600 mt-0.5">
            {spec.material_kind || '—'} · {spec.profile || '—'}
          </div>
          {snap.grand_total ? (
            <div className="text-xs text-slate-500 mt-1">
              ${snap.grand_total.toLocaleString()} · {snap.line_items?.length || 0} line(s)
            </div>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-200">
        <label
          className="text-[11px] inline-flex items-center gap-1 cursor-pointer text-slate-600"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={!!spec.include_in_summary}
            onChange={onToggleSummary}
            data-testid="reroof-spec-summary-toggle"
          /> In summary
        </label>
        <button
          onClick={(e) => { e.stopPropagation(); onSyncDims && onSyncDims(); }}
          disabled={!dimsAvailable}
          className="ml-auto text-sky-500 hover:text-sky-700 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Update this variant's roof m², perimeter, ridge & valley from the current Live Dimensions card"
          data-testid={`reroof-spec-sync-dims-${spec._id}`}
        ><RefreshCw size={13} /></button>
        <button
          onClick={(e) => { e.stopPropagation(); onPrint(); }}
          className="text-slate-400 hover:text-slate-700"
          title="Print this spec as a customer hand-out PDF"
          data-testid="reroof-spec-print"
        ><Printer size={13} /></button>
        <button
          onClick={(e) => { e.stopPropagation(); onClone(); }}
          className="text-slate-400 hover:text-slate-700"
          title="Clone to compare options"
          data-testid="reroof-spec-clone"
        ><Copy size={13} /></button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="text-slate-400 hover:text-rose-600"
          title="Delete spec"
          data-testid="reroof-spec-delete"
        ><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
function SpecEditor({ spec, defaults, dims, api, appraisalId, pushing, onSaveField, onPush, onCloneAsMaterial, onCloneAsGauge, existingCombos, currentGauge }) {
  const navigate = useNavigate();
  // Local form state — debounced save back to backend so the live preview
  // updates as the operator types without spamming PUTs on every keystroke.
  const [form, setForm] = useState(spec);
  const [preview, setPreview] = useState(spec.calc_snapshot || null);
  const [saving, setSaving] = useState(false);
  // Track dirty (unsaved-from-typing) vs saved state so the operator
  // can see at a glance whether their edits have hit the DB. The
  // commit pipeline (onBlur + manual Save button) clears `dirty` and
  // bumps `lastSavedAt`.
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(spec.updated_at ? new Date(spec.updated_at) : null);
  // Skip-bin "Auto-select" UI state — populated by the
  // /reroof-builder/skip-suggestion endpoint click.
  const [skipSuggestBusy, setSkipSuggestBusy] = useState(false);
  const [skipSuggestion, setSkipSuggestion] = useState(null);
  // Pending patch for the autosave queue — fields the operator has
  // changed but not yet blurred. Flushed on a debounce timer (2s) and
  // by the manual Save button.
  const pendingRef = React.useRef({});

  // Reset form ONLY when the active spec identity changes (operator
  // switched to a different spec in the list). We intentionally do NOT
  // re-seed on `spec.calc_snapshot` updates — those fire after every
  // backend save and would wipe any field the operator is currently
  // typing into but hasn't blurred yet. The live preview is kept fresh
  // via the debounced effect below instead.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setForm(spec);
    setPreview(spec.calc_snapshot || null);
  }, [spec._id]);

  // Flashings & Dektites panel edits re-fold into the option total.
  const [panelTick, setPanelTick] = useState(0);
  useEffect(() => {
    const h = () => setPanelTick(k => k + 1);
    window.addEventListener('rtr:child-quote-refresh', h);
    return () => window.removeEventListener('rtr:child-quote-refresh', h);
  }, []);

  // Debounced live-preview recompute as the operator edits
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(
          `/appraisals/${appraisalId}/reroof-builder/preview`, form
        );
        setPreview(data);
      } catch (e) { /* swallow — preview is best-effort */ }
    }, 350);
    return () => clearTimeout(t);
  }, [form, api, appraisalId, panelTick]);

  const commitField = useCallback(async (patch) => {
    setSaving(true);
    try {
      await onSaveField(patch);
      setLastSavedAt(new Date());
      // Anything we just persisted leaves the pending queue.
      pendingRef.current = Object.fromEntries(
        Object.entries(pendingRef.current).filter(([k]) => !(k in patch))
      );
      setDirty(Object.keys(pendingRef.current).length > 0);
    } finally {
      setSaving(false);
    }
  }, [onSaveField]);

  // Manual "Save now" — flushes everything in the pending queue (so the
  // operator never has to blur a field to persist) and shows a flash on
  // both success and failure.
  const handleManualSave = useCallback(async () => {
    if (!dirty && Object.keys(pendingRef.current).length === 0) {
      setLastSavedAt(new Date());
      return;
    }
    const patch = { ...pendingRef.current };
    if (Object.keys(patch).length === 0) return;
    setSaving(true);
    try {
      await onSaveField(patch);
      pendingRef.current = {};
      setDirty(false);
      setLastSavedAt(new Date());
    } finally {
      setSaving(false);
    }
  }, [dirty, onSaveField]);

  // Autosave — 2-second debounce after the last keystroke. Pending patch
  // is queued by `field()` below; this effect drains it.
  React.useEffect(() => {
    if (!dirty) return undefined;
    const t = setTimeout(() => { handleManualSave(); }, 2000);
    return () => clearTimeout(t);
  }, [dirty, form, handleManualSave]);

  const field = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    pendingRef.current = { ...pendingRef.current, [k]: v === '' ? null : v };
    setDirty(true);
  };

  return (
    <div className="bg-white rounded ring-1 ring-slate-200 p-4 space-y-4" data-testid="reroof-spec-editor">
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="text"
          value={form.name || ''}
          onChange={(e) => field('name', e.target.value)}
          onBlur={() => commitField({ name: form.name })}
          className="text-lg font-bold text-[#0F172A] bg-transparent border-b border-slate-300 focus:border-[#D35400] outline-none px-1"
          data-testid="reroof-spec-name-input"
        />
        {form.is_chosen && (
          <span className="px-2 py-0.5 text-xs font-bold text-amber-700 bg-amber-100 rounded-full">★ Chosen quote</span>
        )}
        {/* Save status — auto-save indicator. Shows: spinner during a
            commit, "Unsaved" when dirty, "Saved · HH:MM" once persisted.
            Operator can also press the manual "Save" button to flush
            without waiting for the 2-second debounce. */}
        <span
          className={`text-[11px] font-semibold ${
            saving ? 'text-slate-500' : dirty ? 'text-amber-700' : 'text-emerald-700'
          }`}
          data-testid="reroof-spec-save-status"
        >
          {saving ? (
            <span className="inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Saving…</span>
          ) : dirty ? (
            <span className="inline-flex items-center gap-1">● Unsaved</span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 size={11} /> Saved{lastSavedAt ? ` · ${lastSavedAt.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}` : ''}
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={handleManualSave}
          disabled={saving}
          data-testid="reroof-spec-save-now-btn"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded ring-1 ring-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Save now — flushes any pending edits to the database (autosave also fires 2s after the last keystroke)"
        >
          Save now
        </button>
        <button
          onClick={async () => {
            // iter313c H1 fix — flush any pending (dirty) edits BEFORE
            // hitting the push endpoint. Without this, a Push clicked
            // within the 2s autosave debounce would send the previously
            // saved spec values (not what's on screen) → silent bad
            // quote. handleManualSave() no-ops when pendingRef is empty.
            if (Object.keys(pendingRef.current).length > 0) {
              await handleManualSave();
            }
            onPush();
          }}
          disabled={pushing || saving || dirty}
          className="ml-auto inline-flex items-center gap-2 px-3 py-2 bg-[#D35400] hover:bg-[#A04000] disabled:opacity-50 text-white text-sm font-semibold rounded-md shadow-sm"
          title={dirty ? 'Flush pending edits first (auto-saves in 2s)' : 'Push to Components'}
          data-testid="reroof-push-to-components-btn"
        >
          {pushing ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
          Push to Components
        </button>
      </div>

      {/* Material-variant quick bar — clone this spec into another
          material kind with one click. Useful for offering the client
          Galv vs Zinc vs Colorsteel side-by-side without re-typing
          every field. The new variant carries every other field
          (profile, dimensions, framing) from the current spec. */}
      {onCloneAsMaterial && (defaults.material_kinds || []).length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="reroof-material-variants">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Clone as variant in:</span>
          {(defaults.material_kinds || [])
            .filter((m) => m.key !== form.material_kind)
            .map((m) => {
              // Bob's Test R3 · Bug 4 — colour badge when this
              // {material, gauge} combo already exists as another spec
              // on the same parent appraisal. Click still fires (Q2b
              // answer = "allow the click with a note") — the click
              // handler jumps to the existing variant instead of
              // creating a duplicate.
              const combo = `${m.key}::${currentGauge || '0.40'}`;
              const isDup = existingCombos && existingCombos.has(combo);
              return (
              <button
                key={m.key}
                type="button"
                data-testid={`reroof-clone-variant-${m.key}`}
                onClick={() => onCloneAsMaterial(m.key)}
                className={
                  isDup
                    // Coloured: this combo already exists — click will jump to it.
                    ? "inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-emerald-100 hover:bg-emerald-200 ring-1 ring-emerald-500 rounded font-semibold text-emerald-800"
                    // Default: available for clone
                    : "inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-white hover:bg-slate-50 ring-1 ring-slate-300 rounded font-semibold text-slate-700"
                }
                title={
                  isDup
                    ? `${m.label} @ ${currentGauge || '0.40'} already exists — click to jump to it`
                    : `Create a new spec identical to this one but in ${m.label}`
                }
              >
                <Copy size={11} /> {m.key}{isDup ? ' ✓' : ''}
              </button>
              );
            })}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap" data-testid="reroof-gauge-variants">
        {onCloneAsGauge && (
          <>
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Clone as gauge:</span>
            {['0.40', '0.55'].filter(g => g !== (currentGauge || '0.40')).map(g => (
              <button key={g} type="button" data-testid={`reroof-clone-gauge-${g}`} onClick={() => onCloneAsGauge(g)}
                title={`New spec identical to this one in ${form.material_kind} ${g}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-white hover:bg-slate-50 ring-1 ring-slate-300 rounded font-semibold text-slate-700">
                <Copy size={11} /> {form.material_kind} {g}
              </button>
            ))}
          </>
        )}
        <MakeSeparateQuoteButton api={api} appraisalId={appraisalId} body={{ spec_id: spec._id }}
          label={`${form.material_kind || ''} ${currentGauge || '0.40'}`} testid="reroof-make-separate-quote" />
      </div>

      {/* UAT 2026-02-28 — form was taking most of 1fr and fields
          stretched wider than their text while the 360px preview
          overflowed the line-items table. Cap the form column and let
          the preview flex to the remaining space. */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,460px)_minmax(0,1fr)] gap-4">
        {/* Form */}
        <div className="space-y-3">
          <Field label="Label (free text for quote summary)" testid="reroof-label">
            <input
              type="text"
              value={form.label || ''}
              onChange={(e) => field('label', e.target.value)}
              onBlur={() => commitField({ label: form.label })}
              placeholder='e.g. "Galv corrugate option"'
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
            />
          </Field>

          {/* Re-roof type — drives whether the BOM includes new
              purlins (Tile→Iron) or just a small repair length
              (Iron→Iron). The repair-length input appears below when
              Iron→Iron is selected. */}
          <Field label="Re-roof type" testid="reroof-field-reroof-type">
            <select
              value={form.reroof_type || 'Concrete Tile to Iron'}
              onChange={(e) => { field('reroof_type', e.target.value); commitField({ reroof_type: e.target.value }); }}
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded bg-white"
              data-testid="reroof-reroof-type-select"
            >
              {(defaults.reroof_types || [{ key: 'Concrete Tile to Iron', label: 'Concrete Tile → Iron' }])
                .filter((t) => t.key !== 'Tile to Iron') /* hide legacy alias */
                .map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
            </select>
          </Field>

          {/* Skip-bin supplier picker — only relevant when the re-roof
              type generates non-recyclable waste (Concrete Tile). For
              other types the dump-bin BOM is skipped so the picker
              still saves but doesn't affect totals. */}
          {(defaults.skip_bin_suppliers || []).length > 0 && (
            <Field label="Skip-bin supplier" testid="reroof-field-bin-supplier">
              <div className="flex items-stretch gap-1.5">
                <select
                  data-testid="reroof-bin-supplier-select"
                  value={form.skip_bin_supplier_id || ''}
                  onChange={(e) => { field('skip_bin_supplier_id', e.target.value); commitField({ skip_bin_supplier_id: e.target.value }); }}
                  className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-slate-300 rounded bg-white"
                >
                  <option value="">— Auto (first available) —</option>
                  {(defaults.skip_bin_suppliers || []).map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name}{s.delivery_lead_days ? ` · ${s.delivery_lead_days} day lead` : ''}
                    </option>
                  ))}
                </select>
                {/* One-click smart pick — calls /skip-suggestion which
                    scores every supplier's bin catalogue against the
                    live waste_m3 from the calc. Cheapest plan wins. */}
                <button
                  type="button"
                  data-testid="reroof-skip-autoselect-btn"
                  onClick={async () => {
                    setSkipSuggestBusy(true);
                    setSkipSuggestion(null);
                    try {
                      const { data } = await api.post(
                        `/appraisals/${appraisalId}/reroof-builder/skip-suggestion`,
                        { spec_id: spec._id },
                      );
                      setSkipSuggestion(data);
                      if (data?.best?.supplier_id) {
                        field('skip_bin_supplier_id', data.best.supplier_id);
                        commitField({ skip_bin_supplier_id: data.best.supplier_id });
                      }
                    } catch (e) {
                      setSkipSuggestion({ error: e?.response?.data?.detail || 'Failed' });
                    } finally { setSkipSuggestBusy(false); }
                  }}
                  disabled={skipSuggestBusy}
                  className="px-2.5 py-1.5 text-xs font-semibold text-white bg-[#D35400] hover:bg-[#A04000] disabled:opacity-50 rounded whitespace-nowrap"
                  title="Score every supplier's bins against this roof's waste volume and pick the cheapest viable plan"
                >{skipSuggestBusy ? '…' : '🪣 Auto-select'}</button>
              </div>
              {/* Suggestion result panel — closeable, shows the picked
                  supplier's plan + the top 5 alternatives so the
                  operator can see why this one won. */}
              {skipSuggestion && (
                <div
                  className="mt-2 ring-1 ring-orange-200 bg-orange-50 rounded p-2 text-xs space-y-1.5"
                  data-testid="reroof-skip-suggestion-panel"
                >
                  {skipSuggestion.error ? (
                    <div className="text-rose-700">⚠ {skipSuggestion.error}</div>
                  ) : skipSuggestion.best ? (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-orange-900">
                          ✓ Picked {skipSuggestion.best.supplier_name}
                        </div>
                        <button
                          type="button"
                          onClick={() => setSkipSuggestion(null)}
                          className="text-orange-700 text-[10px] underline"
                        >dismiss</button>
                      </div>
                      <div className="text-orange-800">
                        <span className="font-mono-num">{skipSuggestion.waste_m3} m³</span> waste from{' '}
                        <span className="font-mono-num">{(skipSuggestion.roof_m2 || 0).toFixed(1)} m²</span> roof
                        → ${skipSuggestion.best.total_cost.toLocaleString()} for{' '}
                        <span className="font-mono-num">{skipSuggestion.best.total_capacity_m3} m³</span> capacity
                        {skipSuggestion.best.delivery_lead_days ? ` · ${skipSuggestion.best.delivery_lead_days} day lead` : ''}.
                      </div>
                      <div className="text-[11px] text-orange-700">
                        Plan: {(skipSuggestion.best.bins || []).map((b, i) => (
                          <span key={i} className="mr-1.5">
                            {b.count} × {b.label} (${b.subtotal.toLocaleString()})
                          </span>
                        ))}
                      </div>
                      {Array.isArray(skipSuggestion.alternatives) && skipSuggestion.alternatives.length > 0 && (
                        <details className="text-[11px] text-orange-700 mt-1">
                          <summary className="cursor-pointer font-semibold">Top {skipSuggestion.alternatives.length} alternatives</summary>
                          <ul className="mt-1 space-y-0.5">
                            {skipSuggestion.alternatives.map((a) => (
                              <li key={a.supplier_id} className="flex items-center gap-2">
                                <button
                                  type="button"
                                  data-testid={`reroof-skip-alt-${a.supplier_id}`}
                                  onClick={() => {
                                    field('skip_bin_supplier_id', a.supplier_id);
                                    commitField({ skip_bin_supplier_id: a.supplier_id });
                                  }}
                                  className="text-orange-800 underline hover:text-orange-900"
                                  title="Switch to this supplier"
                                >{a.supplier_name}</button>
                                <span className="text-orange-600">${a.total_cost.toLocaleString()} · {a.total_capacity_m3} m³{a.delivery_lead_days ? ` · ${a.delivery_lead_days} day` : ''}</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </>
                  ) : (
                    <div className="text-orange-800">{skipSuggestion.reason || 'No suggestion available.'}</div>
                  )}
                </div>
              )}
            </Field>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Profile" testid="reroof-field-profile">
              <select
                value={form.profile || 'Corrugate'}
                onChange={(e) => { field('profile', e.target.value); commitField({ profile: e.target.value }); }}
                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
                data-testid="reroof-profile-select"
              >
                {/* Office spec: only Corrugate exposed for Re-roof Builder
                    today. Other profiles still supported in the backend
                    rate-card so we can re-enable later by widening this
                    filter. */}
                {(defaults.profiles || []).filter((p) => p.key === 'Corrugate').map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.key} · cover {p.cover_m} m · {p.fastener_type}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Material kind" testid="reroof-field-material">
              <select
                value={form.material_kind || ''}
                onChange={(e) => { field('material_kind', e.target.value); commitField({ material_kind: e.target.value }); }}
                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
                data-testid="reroof-material-select"
              >
                {(defaults.material_kinds || []).map((m) => (
                  <option key={m.key} value={m.key}>{m.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Gauge" testid="reroof-field-gauge">
              {/* R2 (2026-02) · Bob's Test — gauge sub-variant selector.
                  0.40 is the default; 0.55 is the heavier sub-variant.
                  Flows into the Component-Rate-Tables lookup key so the
                  correct rate row is resolved on push-to-components. */}
              <select
                value={form.gauge || '0.40'}
                onChange={(e) => { field('gauge', e.target.value); commitField({ gauge: e.target.value }); }}
                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
                data-testid="reroof-gauge-select"
              >
                <option value="0.40">0.40 (standard)</option>
                <option value="0.55">0.55 (heavy)</option>
              </select>
            </Field>
            <Field label="Brand" testid="reroof-field-brand">
              <input
                type="text"
                value={form.brand || ''}
                onChange={(e) => field('brand', e.target.value)}
                onBlur={() => commitField({ brand: form.brand })}
                placeholder="Colorsteel / Roofing Industries / Gerard"
                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
              />
            </Field>
            <Field label="Colour" testid="reroof-field-colour">
              {/* Colour picker — backed by live /api/dropdowns/roof_colours
                  list. Falls back to a free-text input when the dropdown
                  is empty so the operator is never stuck. */}
              {(defaults.roof_colours || []).length > 0 ? (
                <select
                  value={form.colour || ''}
                  onChange={(e) => { field('colour', e.target.value); commitField({ colour: e.target.value }); }}
                  className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded bg-white"
                  data-testid="reroof-colour-select"
                >
                  <option value="">— Select colour —</option>
                  {(defaults.roof_colours || []).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={form.colour || ''}
                  onChange={(e) => field('colour', e.target.value)}
                  onBlur={() => commitField({ colour: form.colour })}
                  placeholder='e.g. "Karaka" / "Unpainted Zinc"'
                  className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
                />
              )}
            </Field>
            <Field label="Framing type" testid="reroof-field-framing">
              <select
                value={form.framing_type || ''}
                onChange={(e) => { field('framing_type', e.target.value); commitField({ framing_type: e.target.value }); }}
                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
              >
                {(defaults.framing_types || []).map((f) => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Underlay" testid="reroof-field-underlay">
              <select
                value={form.underlay_type || ''}
                onChange={(e) => { field('underlay_type', e.target.value); commitField({ underlay_type: e.target.value }); }}
                className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
              >
                {(defaults.underlay_options || []).map((u) => (
                  <option key={u.key} value={u.key}>{u.key} · ${u.rate_per_m2}/m²</option>
                ))}
              </select>
            </Field>
          </div>

          {/* Framing detail — operator types in mm-precision rafter and
              purlin spans. `purlin_span_mm` overrides `purlin_centres_mm`
              in the BOM calc when set. Iron→Iron also exposes the
              "Repair length" input for spot purlin replacement. */}
          <div className="pt-2 border-t border-slate-200">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Framing detail</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Rafter span (mm)" testid="reroof-field-rafter-span">
                <input
                  type="number" min={300} max={2400} step={50}
                  list="reroof-rafter-span-options"
                  data-testid="reroof-rafter-span-input"
                  value={form.rafter_span_mm ?? ''}
                  onChange={(e) => field('rafter_span_mm', e.target.value === '' ? '' : Number(e.target.value))}
                  onBlur={() => commitField({ rafter_span_mm: form.rafter_span_mm === '' ? null : Number(form.rafter_span_mm) })}
                  placeholder="e.g. 900"
                  className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono-num"
                />
                <datalist id="reroof-rafter-span-options">
                  <option value="600" />
                  <option value="750" />
                  <option value="900" />
                  <option value="1200" />
                </datalist>
              </Field>
              <Field label="Purlin span (mm)" testid="reroof-field-purlin-span">
                <input
                  type="number" min={300} max={2400} step={50}
                  list="reroof-purlin-span-options"
                  data-testid="reroof-purlin-span-input"
                  value={form.purlin_span_mm ?? ''}
                  onChange={(e) => field('purlin_span_mm', e.target.value === '' ? '' : Number(e.target.value))}
                  onBlur={() => commitField({ purlin_span_mm: form.purlin_span_mm === '' ? null : Number(form.purlin_span_mm) })}
                  placeholder="e.g. 1200"
                  className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono-num"
                />
                <datalist id="reroof-purlin-span-options">
                  <option value="600" />
                  <option value="750" />
                  <option value="900" />
                  <option value="1200" />
                </datalist>
              </Field>
              {form.reroof_type === 'Iron to Iron' && (
                <Field label="Repair purlins (m)" testid="reroof-field-purlin-repair">
                  <input
                    type="number" min={0} step={0.5}
                    data-testid="reroof-purlin-repair-input"
                    value={form.purlin_repair_m ?? 0}
                    onChange={(e) => field('purlin_repair_m', e.target.value === '' ? '' : Number(e.target.value))}
                    onBlur={() => commitField({ purlin_repair_m: form.purlin_repair_m === '' ? 0 : Number(form.purlin_repair_m) })}
                    placeholder="e.g. 8"
                    className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded font-mono-num"
                  />
                </Field>
              )}
              {/* Shadowed Purlin Estimate Total — live computed from
                  roof_m² ÷ purlin_span_m + ridge×2 + valley×2. Same
                  formula the backend BOM uses; this is just an early
                  preview the operator can sanity-check BEFORE running
                  the full preview. Grey-shaded to signal "estimate
                  only", with a "Use this" button so they can commit
                  it as the `purlin_total_m_override` if the visual
                  count from the diagram differs. */}
              <Field label="Purlin estimate total (m)" testid="reroof-field-purlin-estimate">
                {(() => {
                  const rt = (form.reroof_type || '').toLowerCase();
                  const isRepairOnly = rt === 'repair only';
                  const isNewRoof = rt === 'new roof';
                  const isIronToIron = rt === 'iron to iron';
                  const purlinSpanMm = Number(form.purlin_span_mm || form.purlin_centres_mm || 900);
                  const roofM2 = Number(form.roof_m2 || 0);
                  const ridgeM = Number(form.ridge_total_m || 0);
                  const valleyM = Number(form.valley_total_m || 0);
                  const repairM = Number(form.purlin_repair_m || 0);
                  let est = 0;
                  let breakdown = '';
                  if (isRepairOnly) {
                    est = 0;
                    breakdown = 'Repair Only — no purlins required';
                  } else if (isNewRoof || isIronToIron) {
                    est = repairM;
                    breakdown = `${isNewRoof ? 'New Roof' : 'Iron → Iron'} — repair length only`;
                  } else if (purlinSpanMm > 0 && roofM2 > 0) {
                    const facePurlinM = roofM2 / (purlinSpanMm / 1000);
                    const ridgePurlinM = ridgeM * 2;
                    const valleyPurlinM = valleyM * 2;
                    est = facePurlinM + ridgePurlinM + valleyPurlinM;
                    breakdown = `${facePurlinM.toFixed(1)} face + ${ridgePurlinM.toFixed(1)} ridge + ${valleyPurlinM.toFixed(1)} valley`;
                  } else {
                    breakdown = 'Set roof m² + purlin span to estimate';
                  }
                  const hasOverride = form.purlin_total_m_override != null
                                    && form.purlin_total_m_override !== ''
                                    && Number(form.purlin_total_m_override) > 0;
                  return (
                    <div className="relative">
                      <input
                        type="text"
                        readOnly
                        data-testid="reroof-purlin-estimate-output"
                        value={`${est.toFixed(1)} m`}
                        className="w-full px-2 py-1.5 text-sm rounded font-mono-num bg-slate-100 text-slate-700 border border-dashed border-slate-300 cursor-default"
                        title={breakdown}
                      />
                      <div className="text-[10px] text-slate-500 mt-0.5 truncate" title={breakdown}>
                        {breakdown}
                      </div>
                      {est > 0 && !isRepairOnly && (
                        <button
                          type="button"
                          data-testid="reroof-purlin-estimate-commit"
                          onClick={() => {
                            const rounded = Math.round(est * 10) / 10;
                            field('purlin_total_m_override', rounded);
                            commitField({ purlin_total_m_override: rounded });
                          }}
                          className={`mt-1 text-[10px] px-2 py-0.5 rounded font-bold inline-flex items-center gap-1 transition-colors ${
                            hasOverride
                              ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                              : 'bg-orange-50 text-orange-700 ring-1 ring-orange-200 hover:bg-orange-100'
                          }`}
                          title={hasOverride
                            ? `Override active: ${Number(form.purlin_total_m_override).toFixed(1)} m. Click to refresh from current estimate.`
                            : 'Commit this estimate as the BOM total (overrides auto-calc inside the preview).'
                          }
                        >
                          {hasOverride ? '✓ Override set' : 'Use this estimate'}
                        </button>
                      )}
                    </div>
                  );
                })()}
              </Field>
            </div>
            {/* Optional: clear override so the BOM goes back to live auto-calc */}
            {form.purlin_total_m_override != null && form.purlin_total_m_override !== '' && Number(form.purlin_total_m_override) > 0 && (
              <button
                type="button"
                data-testid="reroof-purlin-estimate-clear"
                onClick={() => {
                  field('purlin_total_m_override', null);
                  commitField({ purlin_total_m_override: null });
                }}
                className="mt-2 text-[10px] text-slate-500 hover:text-rose-700 hover:underline"
              >
                Clear purlin total override (revert to auto-calc)
              </button>
            )}
            {/* Standalone Purlin Calculator — full per-face grid layout */}
            <div className="mt-2 pt-2 border-t border-slate-200">
              <button
                type="button"
                data-testid="reroof-open-purlin-calculator"
                onClick={() => navigate(`/appraisals/${appraisalId}/reroof-specs/${spec._id}/purlin-calculator`)}
                className="text-xs font-semibold inline-flex items-center gap-1.5 bg-orange-50 border border-orange-300 text-orange-700 hover:bg-orange-100 rounded-md px-3 py-1.5"
                title="Open the full per-face Purlin Calculator — grid-up each roof face, pick stock lengths, compute the order."
              >
                <Calculator size={13} /> Open Purlin Calculator →
              </button>
              <span className="text-[10px] text-slate-500 ml-2">
                For complex rooves with multiple faces of different sizes.
              </span>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-200">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide">Dimensions (override diagram)</h4>
              {/* "Use all estimates" — copies every live-dim estimate
                  (roof m², perimeter, ridge, valley, spouting, barges,
                  pipes count + m, flues count) into the form's override
                  fields in one click. Mirrors the per-field "Use this"
                  buttons but in bulk. */}
              {(() => {
                const candidates = {
                  roof_m2: Number(dims?.face_m2 || 0),
                  perimeter_m: Number(dims?.perimeter_m || 0),
                  ridge_total_m: Number(dims?.ridge_total_m || 0),
                  valley_total_m: Number(dims?.valley_total_m || 0),
                  spouting_total_m: Number(dims?.spouting_total_m || 0),
                  barge_total_m: Number(dims?.barge_total_m || 0),
                  pipes_total_m: Number(dims?.pipes_total_m || 0),
                  pipes_count: Number(dims?.pipes_count || 0),
                  flues_count: Number(dims?.flues_count || 0),
                };
                const diffs = Object.entries(candidates).filter(([k, v]) => {
                  if (!isFinite(v)) return false;
                  const curr = Number(form[k] || 0);
                  return Math.abs(curr - v) > 0.005;
                });
                const noDims = !dims;
                const allMatch = !noDims && diffs.length === 0;
                return (
                  <button
                    type="button"
                    data-testid="reroof-use-all-estimates"
                    disabled={noDims}
                    onClick={() => {
                      const patch = {};
                      Object.entries(candidates).forEach(([k, v]) => {
                        if (!isFinite(v)) return;
                        const rounded = Math.round(v * 100) / 100;
                        patch[k] = rounded;
                        field(k, rounded);
                      });
                      commitField(patch);
                    }}
                    title={noDims
                      ? 'Live Dimensions not loaded yet'
                      : allMatch
                        ? 'All override fields already match the diagram estimates'
                        : `Overwrite ${diffs.length} override field${diffs.length === 1 ? '' : 's'} with the current Live Dimensions: ${diffs.map(([k]) => k.replace(/_total_m$/, '').replace(/_m2$/, ' m²').replace(/_count$/, ' #').replace(/_/g, ' ')).join(', ')}`}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      allMatch
                        ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                        : 'bg-orange-50 text-orange-700 ring-1 ring-orange-300 hover:bg-orange-100'
                    }`}
                  >
                    <RefreshCw size={11} />
                    {allMatch ? 'All estimates applied ✓' : `Use all estimates${diffs.length ? ` (${diffs.length})` : ''}`}
                  </button>
                );
              })()}
            </div>
            {/* Source quick-fill — Roof m² can come from the diagram
                (auto-Face-pin sum) OR from the appraisal's Roof Details
                tab. Office prefers the takeoff-sheet value (~202.86 m²)
                when no Face pins exist on the diagram. */}
            <div className="flex items-center gap-2 mb-2 flex-wrap text-[11px]" data-testid="reroof-roof-m2-source">
              <span className="text-slate-500">Roof m² source:</span>
              <button
                type="button"
                data-testid="reroof-source-diagram"
                onClick={() => {
                  const v = Number(dims?.face_m2 || 0);
                  field('roof_m2', v);
                  commitField({ roof_m2: v });
                }}
                className={`px-2 py-0.5 rounded ring-1 ${
                  Math.abs(Number(form.roof_m2 || 0) - Number(dims?.face_m2 || 0)) < 0.01
                    ? 'bg-emerald-600 text-white ring-emerald-600'
                    : 'bg-white text-slate-700 ring-slate-300 hover:bg-slate-50'
                }`}
                title="Use the diagram-computed face area"
              >
                Diagram ({(dims?.face_m2 || 0).toFixed(1)} m²)
              </button>
              <button
                type="button"
                data-testid="reroof-source-roof-details"
                onClick={() => {
                  const v = Number(dims?.appraisal_roof_details_m2 || 0);
                  field('roof_m2', v);
                  commitField({ roof_m2: v });
                }}
                disabled={!dims?.appraisal_roof_details_m2}
                className={`px-2 py-0.5 rounded ring-1 disabled:opacity-40 disabled:cursor-not-allowed ${
                  dims?.appraisal_roof_details_m2 && Math.abs(Number(form.roof_m2 || 0) - Number(dims.appraisal_roof_details_m2)) < 0.01
                    ? 'bg-emerald-600 text-white ring-emerald-600'
                    : 'bg-white text-slate-700 ring-slate-300 hover:bg-slate-50'
                }`}
                title={dims?.appraisal_roof_details_m2 ? 'Use the sum of every Roof Details record' : 'No Roof Details m² recorded on this appraisal'}
              >
                Roof Details ({(dims?.appraisal_roof_details_m2 || 0).toFixed(1)} m²)
              </button>
              {dims?.appraisal_roof_details_m2_confidence && (
                <RoofM2ConfidenceBanner
                  api={api}
                  appraisalId={appraisalId}
                  source={dims.appraisal_roof_details_m2_source}
                  confidence={dims.appraisal_roof_details_m2_confidence}
                  label="Roof m²"
                  compact
                />
              )}
              <span className="text-slate-400 italic">— or type any value below</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ['roof_m2', 'Roof m²', 'face_m2', 'm²'],
                ['perimeter_m', 'Perimeter m', 'perimeter_m', 'm'],
                ['ridge_total_m', 'Ridging m (ridge + hip)', 'ridging_total_m', 'm'],
                ['valley_total_m', 'Valley m', 'valley_total_m', 'm'],
                ['spouting_total_m', 'Spouting m', 'spouting_total_m', 'm'],
                ['barge_total_m', 'Barges m', 'barge_total_m', 'm'],
                ['pipes_total_m', 'Pipes m', 'pipes_total_m', 'm'],
                ['pipes_count', 'Pipes #', 'pipes_count', ''],
                ['flues_count', 'Flues #', 'flues_count', ''],
                ['purlin_span_mm', 'Purlin span (mm)', null, 'mm'],
                ['screws_per_m2', 'Screws / m²', null, ''],
                ['off_cut_pct', 'Off-cut %', null, '%'],
                ['junction_count', 'Junction count', null, ''],
              ].map(([k, label, dimsKey, unit]) => {
                // Screws / m² is a fixed dropdown — 6/7/8/9 — with the
                // profile default shown as the placeholder. Operator
                // can clear it back to the default via the (default)
                // option.
                if (k === 'screws_per_m2') {
                  const profileDefault = defaults?.profiles?.find(
                    (p) => p.key === (form.profile || 'Corrugate')
                  )?.screws_per_m2 || 7;
                  return (
                    <Field key={k} label={`${label} (default: Component Rate Tables ratio, else ${profileDefault})`} testid={`reroof-${k}`}>
                      <select
                        value={form[k] ?? ''}
                        onChange={(e) => {
                          const v = e.target.value === '' ? null : Number(e.target.value);
                          field(k, v === null ? '' : v);
                          commitField({ [k]: v });
                        }}
                        className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded bg-white"
                        data-testid={`reroof-${k}-select`}
                      >
                        <option value="">Default ({profileDefault})</option>
                        <option value={6}>6 screws / m²</option>
                        <option value={7}>7 screws / m²</option>
                        <option value={8}>8 screws / m²</option>
                        <option value={9}>9 screws / m²</option>
                      </select>
                    </Field>
                  );
                }
                // Purlin span — dropdown (matches Framing Detail
                // dropdown convention). Replaces the legacy free-typed
                // "Purlin ctrs (mm)" field.
                if (k === 'purlin_span_mm') {
                  const SPAN_OPTS = [600, 750, 900, 1200];
                  const v = Number(form[k] || 0);
                  const isCustom = v > 0 && !SPAN_OPTS.includes(v);
                  return (
                    <Field key={k} label={label} testid={`reroof-${k}`}>
                      <div className="flex gap-1">
                        <select
                          value={isCustom ? '__custom' : (v ? String(v) : '')}
                          onChange={(e) => {
                            if (e.target.value === '__custom') return;
                            const nv = e.target.value === '' ? null : Number(e.target.value);
                            field(k, nv === null ? '' : nv);
                            commitField({ [k]: nv });
                          }}
                          data-testid={`reroof-${k}-select`}
                          className="flex-1 px-2 py-1.5 text-sm border border-slate-300 rounded bg-white font-mono-num"
                        >
                          <option value="">— pick —</option>
                          {SPAN_OPTS.map((o) => <option key={o} value={String(o)}>{o} mm</option>)}
                          <option value="__custom">Custom…</option>
                        </select>
                        {isCustom && (
                          <input
                            type="number" min={100} max={3000} step={50}
                            value={v}
                            data-testid={`reroof-${k}-custom`}
                            onChange={(e) => field(k, e.target.value === '' ? '' : Number(e.target.value))}
                            onBlur={() => commitField({ [k]: form[k] === '' ? null : Number(form[k]) })}
                            className="w-20 px-1.5 py-1.5 text-xs border border-slate-300 rounded font-mono-num"
                          />
                        )}
                      </div>
                    </Field>
                  );
                }
                // Diagram-derived estimate (shown beneath the input
                // with a "Use this" button) for the 4 measurable
                // dimensions.
                const estVal = dimsKey ? Number(dims?.[dimsKey] || 0) : 0;
                const curVal = Number(form[k] || 0);
                const matchesEst = estVal > 0 && Math.abs(curVal - estVal) < 0.01;
                const extraProps = k === 'roof_m2' ? {
                  onDoubleClick: () => {
                    const v2 = Number(dims?.appraisal_roof_details_m2 || 0);
                    if (v2 > 0) {
                      field('roof_m2', v2);
                      commitField({ roof_m2: v2 });
                    }
                  },
                  title: dims?.appraisal_roof_details_m2
                    ? `Double-click to fill from Roof Details (${(dims.appraisal_roof_details_m2 || 0).toFixed(1)} m²)`
                    : 'Roof Details has no m² recorded yet',
                } : {};
                return (
                  <Field key={k} label={label} testid={`reroof-${k}`}>
                    <input
                      type="number"
                      value={form[k] ?? ''}
                      onChange={(e) => field(k, e.target.value === '' ? '' : Number(e.target.value))}
                      onBlur={() => commitField({ [k]: form[k] === '' ? null : Number(form[k]) })}
                      placeholder={
                        k === 'junction_count' ? '(auto)'
                        : (estVal > 0 ? `${estVal}${unit ? ` ${unit}` : ''} (diagram)` : '')
                      }
                      className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
                      {...extraProps}
                      data-testid={k === 'roof_m2' ? 'reroof-roof-m2-input' : undefined}
                    />
                    {dimsKey && estVal > 0 && (
                      <button
                        type="button"
                        data-testid={`reroof-${k}-estimate-use`}
                        onClick={() => {
                          const rounded = k.endsWith('_count')
                            ? Math.round(estVal)
                            : Math.round(estVal * 100) / 100;
                          field(k, rounded);
                          commitField({ [k]: rounded });
                        }}
                        title={matchesEst
                          ? `Override matches the diagram estimate (${k.endsWith('_count') ? Math.round(estVal) : estVal.toFixed(2)} ${unit})`
                          : `Click to overwrite with diagram estimate (${k.endsWith('_count') ? Math.round(estVal) : estVal.toFixed(2)} ${unit})`}
                        className={`mt-1 w-full text-[10px] rounded px-1.5 py-0.5 inline-flex items-center justify-between gap-1 transition-colors ${
                          matchesEst
                            ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                            : 'bg-slate-100 text-slate-600 ring-1 ring-slate-200 hover:bg-orange-50 hover:text-orange-700 hover:ring-orange-200'
                        }`}
                      >
                        <span className="italic">Estimate: {k.endsWith('_count') ? Math.round(estVal) : estVal.toFixed(2)} {unit}</span>
                        <span className="font-bold">{matchesEst ? '✓' : 'Use this'}</span>
                      </button>
                    )}
                  </Field>
                );
              })}
            </div>
          </div>

          <Field label="Notes / sales messaging" testid="reroof-field-notes">
            <textarea
              value={form.notes || ''}
              onChange={(e) => field('notes', e.target.value)}
              onBlur={() => commitField({ notes: form.notes })}
              rows={2}
              className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
            />
          </Field>

          {/* v10.29 Sprint 4a · Slice H (2026-02c) — Misc Hours multi-entry.
              Fixed catalogue rate (3 options); operator adds any number
              of labour entries, each with work-type + location + note
              reference points. Sticky across Push (uses
              import_source='reroof_builder_misc_hours'). */}
          <MiscHoursSection
            entries={form.misc_hours || []}
            onChange={(next) => {
              field('misc_hours', next);
              commitField({ misc_hours: next });
            }}
            saving={saving}
          />
        </div>

        {/* Live preview */}
        <div className="space-y-3 min-w-0">
          <PreviewPanel preview={preview} />
          {preview && <QuoteTotalStrip api={api} appraisalId={appraisalId} linesTotal={preview.grand_total} label="This spec's lines" testid="reroof-quote-total-strip" />}
        </div>

        <div className="xl:col-span-2 bg-white rounded ring-1 ring-slate-200 p-3" data-testid="reroof-flashings-panel">
          <ComponentsFlashingEmbed api={api} appraisalId={appraisalId} testid="reroof-components-flashing" />
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
function Field({ label, children, testid }) {
  return (
    <label className="block" {...(testid ? { 'data-testid': testid } : {})}>
      <span className="block text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-0.5">{label}</span>
      {children}
    </label>
  );
}

function PreviewPanel({ preview }) {
  // v10.29 Sprint 4a · Slice C (2026-02c) — Fast-jump navigator. Uses
  // React Router's `?tab=Components&focus_section=<wt>` URL contract.
  // The Components tab has a matching useEffect that reads
  // `focus_section` on mount + URL-change, expands the section
  // accordion, scrolls it into view + flashes a 2s highlight, then
  // clears the param so refresh doesn't retry.
  const navigate = useNavigate();
  const jumpToComponentsSection = (componentType) => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', 'Components');
    params.set('focus_section', componentType);
    navigate(`?${params.toString()}`, { replace: false });
  };
  if (!preview) {
    return (
      <div className="p-4 bg-slate-50 rounded ring-1 ring-slate-200 text-center text-sm text-slate-500" data-testid="reroof-preview-empty">
        Type any field to compute the cost breakdown.
      </div>
    );
  }
  const { line_items = [], materials_total = 0, labour_total = 0, grand_total = 0, purlin = {}, tek_screws_count = 0, blue_screws_count = 0, junction_count = 0, framing_label = '', dims_used = {}, stripping = {}, screws_per_m2 = 0, junction_source = '', screws_per_junction = 0 } = preview;
  const skipPlan = stripping?.skip_plan || {};
  return (
    <CalcShadowProvider defaultShow={true}>
    <div className="space-y-3" data-testid="reroof-preview-panel">
      <div className="flex justify-end">
        <CalcShadowToggle testid="reroof-preview-calcs-toggle" />
      </div>
      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded ring-1 ring-emerald-200 p-3">
        <div className="text-[10px] uppercase tracking-wide font-bold text-emerald-700">Grand total</div>
        <div className="text-2xl font-bold text-emerald-900" data-testid="reroof-grand-total">${grand_total.toLocaleString()}</div>
        <div className="text-[11px] text-emerald-700 mt-1">
          {/* v10.18 (Feb 2026) · Iter317 Batch B folded install labour
              into every component's rate — there is no standalone
              Labour line any more. Old "Materials $X · Labour $0"
              label misled the operator into thinking labour was
              missing. Combined label reflects the current reality.
              v10.29 Sprint 4a · Slice A (2026-02c) — the operator
              specifically asked to see the split; render as two
              clearly-labelled sub-lines with wording that avoids the
              old ambiguity (labour_total is the *standalone* extra
              labour lines, not the combined install labour). */}
          Combined materials + install labour · GST excl
        </div>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="reroof-grand-total-split">
          <div className="text-[11px] px-2 py-0.5 rounded bg-white/70 ring-1 ring-emerald-200"
               data-testid="reroof-split-materials">
            <span className="text-emerald-700 font-semibold">Goods + install labour: </span>
            <span className="font-mono-num font-bold text-emerald-900">${materials_total.toLocaleString()}</span>
          </div>
          {labour_total > 0 && (
            <div className="text-[11px] px-2 py-0.5 rounded bg-white/70 ring-1 ring-emerald-200"
                 data-testid="reroof-split-extra-labour">
              <span className="text-emerald-700 font-semibold">Extra labour: </span>
              <span className="font-mono-num font-bold text-emerald-900">${labour_total.toLocaleString()}</span>
            </div>
          )}
        </div>
        <CalcShadow testid="reroof-grand-total-calc" tone="emerald">
          Σ line-items = <strong>${grand_total.toLocaleString()}</strong> (each line already includes materials rate + install labour rate)
        </CalcShadow>
      </div>

      {/* Strip / Dump / Scrap summary — only shown when there's something
          to summarise (Repair Only skips this panel entirely). */}
      {(stripping?.total > 0 || stripping?.waste_m3 > 0 || stripping?.scrap_credit > 0) && (
        <div className="bg-amber-50 rounded ring-1 ring-amber-200 p-3 text-xs space-y-1.5" data-testid="reroof-strip-dump-panel">
          <div className="text-[10px] uppercase font-bold text-amber-700 tracking-wide flex items-center gap-1.5">
            Strip &amp; dump · {stripping.old_material || 'old roof'}
          </div>
          <Row k="Strip labour" v={`$${(stripping.total || 0).toLocaleString()} @ $${stripping.rate_per_m2 || 0}/m²`} />
          <CalcShadow testid="reroof-strip-labour-calc" tone="amber">
            {(dims_used.roof_m2 || 0).toFixed(1)} m² × ${stripping.rate_per_m2 || 0}/m² = <strong>${(stripping.total || 0).toLocaleString()}</strong>
          </CalcShadow>
          {stripping.waste_m3 > 0 && (
            <>
              <Row k="Waste volume" v={`${(stripping.waste_m3 || 0).toFixed(2)} m³ (${(stripping.waste_m3_per_m2 || 0).toFixed(3)} m³/m²)`} />
              <CalcShadow testid="reroof-waste-calc" tone="amber">
                {(dims_used.roof_m2 || 0).toFixed(1)} m² × {(stripping.waste_m3_per_m2 || 0).toFixed(3)} m³/m² = <strong>{(stripping.waste_m3 || 0).toFixed(2)} m³</strong>
              </CalcShadow>
            </>
          )}
          {skipPlan.bins && skipPlan.bins.length > 0 && (
            <>
              <Row
                k="Skip-bin plan"
                v={
                  <>
                    {skipPlan.bins.map((b, i) => (
                      <span key={i} className="inline-block mr-1.5">
                        {b.count} × {b.label} (${b.subtotal.toLocaleString()})
                      </span>
                    ))}
                  </>
                }
              />
              <Row k="Bin capacity" v={`${(skipPlan.total_capacity_m3 || 0).toFixed(1)} m³ for ${(stripping.waste_m3 || 0).toFixed(2)} m³ waste`} />
              <Row k="Bin total" v={`$${(skipPlan.total_cost || 0).toLocaleString()}`} />
              {stripping.skip_bin_supplier_name && (
                <Row k="Supplier" v={`${stripping.skip_bin_supplier_name}${stripping.skip_bin_lead_days ? ` · ${stripping.skip_bin_lead_days} day lead` : ''}`} />
              )}
            </>
          )}
          {(stripping.scrap_credit > 0 || stripping.scrap_tonnes > 0) && (
            <>
              {/* v10.29 Sprint 1 · B11 — Scrap value is a ROOFER BONUS,
                  not a customer credit. Do NOT display as green negative
                  next to the customer total. Show as an internal
                  planning estimate for trailer loads + roofer takeaway. */}
              <div className="mt-2 pt-2 border-t border-dashed border-amber-300">
                <div className="text-[10px] uppercase font-bold text-amber-700 tracking-wide mb-1">
                  🔩 Roofer takeaway · internal only · NOT applied to customer total
                </div>
                <Row k="Recyclable material"
                     v={`${(stripping.scrap_tonnes || 0).toFixed(2)} tonnes · ≈ ${stripping.scrap_trailer_loads || 0} trailer load${(stripping.scrap_trailer_loads || 0) === 1 ? '' : 's'}`} />
                <Row k="Recycler payout (roofer)"
                     v={<span className="text-slate-600 font-semibold">≈ ${(stripping.scrap_credit || 0).toLocaleString()}</span>} />
                <CalcShadow testid="reroof-scrap-calc" tone="amber">
                  {(dims_used.roof_m2 || 0).toFixed(1)} m² × ${(stripping.scrap_value_per_m2 || 0).toFixed(2)}/m² = <strong>${(stripping.scrap_credit || 0).toLocaleString()}</strong>
                  <span className="block mt-1 text-[10px] italic text-slate-500">Payout goes to the roofer for delivering scrap to the recycler — the customer quote is unchanged.</span>
                </CalcShadow>
              </div>
            </>
          )}
        </div>
      )}

      <div className="bg-white rounded ring-1 ring-slate-200 p-3 text-xs space-y-1.5">
        <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">Take-off summary</div>
        <Row k="Roof area" v={`${(dims_used.roof_m2 || 0).toFixed(1)} m²`} />
        <Row k="Purlin timber" v={`${purlin.pieces_6m || 0} × 6.0m + ${purlin.pieces_4_8m || 0} × 4.8m (${(purlin.total_stock_m || 0).toFixed(1)} m)`} />
        {purlin.raw_total_m > 0 && (
          <CalcShadow testid="reroof-purlin-calc">
            raw {(purlin.raw_total_m || 0).toFixed(1)} m × (1 + offcut) → padded {(purlin.padded_m || 0).toFixed(1)} m → packed into {purlin.pieces_6m || 0} × 6m + {purlin.pieces_4_8m || 0} × 4.8m = <strong>{(purlin.total_stock_m || 0).toFixed(1)} m stock</strong>
          </CalcShadow>
        )}
        <Row k="Junctions" v={`${junction_count} (${framing_label})`} />
        {junction_count > 0 && (
          <CalcShadow testid="reroof-junctions-calc">
            {junction_source === 'from-spans'
              ? <>⌈{(dims_used.roof_m2 || 0).toFixed(1)} ÷ (rafter_span × purlin_span)⌉ = <strong>{junction_count}</strong> junctions</>
              : junction_source === 'operator-override'
                ? <>operator override: <strong>{junction_count}</strong> junctions</>
                : <>{(dims_used.roof_m2 || 0).toFixed(1)} m² × framing density = <strong>{junction_count}</strong> junctions</>}
          </CalcShadow>
        )}
        <Row k="Blue purlin screws" v={`${blue_screws_count}`} />
        {blue_screws_count > 0 && (
          <CalcShadow testid="reroof-blue-screws-calc">
            {junction_count} junctions × {screws_per_junction} screws/junction = <strong>{blue_screws_count}</strong>
          </CalcShadow>
        )}
        <Row k="TekScrews" v={`${tek_screws_count}`} />
        {tek_screws_count > 0 && (
          <CalcShadow testid="reroof-tek-screws-calc">
            ⌈{(dims_used.roof_m2 || 0).toFixed(1)} m² × {screws_per_m2 || 0}/m²⌉ = <strong>{tek_screws_count}</strong>
          </CalcShadow>
        )}
      </div>

      <div className="bg-white rounded ring-1 ring-slate-200 p-3" data-testid="reroof-preview-lines">
        <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wide mb-1.5">Line items pushed to Components</div>
        <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-slate-500">
              <th className="py-1 pr-2">Type</th><th className="py-1 pr-2">Item</th>
              <th className="py-1 pr-2 text-right">Qty</th>
              <th className="py-1 pr-2 text-right">Rate</th>
              <th className="py-1 pr-2 text-right">Total</th>
              <th className="py-1 w-8 text-center" title="Fast-jump to this section in Components tab">↗</th>
            </tr>
          </thead>
          <tbody>
            {line_items.map((li, i) => {
              // Iter317 · Batch G (2026-02 · A6903 · Bob) — Unit-native
              // qty display. Previously every linear/sheet row rendered
              // `qty × length.toFixed(2) m` — but Purlin / Stripping /
              // Vortex Fastening / Labour emit with `length=0` (their
              // quantity IS the total measure), producing the misleading
              // "412.8 × 0.00 m" or "248 × 0.00 m". Rule: only show the
              // "qty × length m" shape when BOTH sides are non-zero
              // (dimensional emit — Sheet siblings from face pins,
              // per-pin Ridge/Barge/Valley). Otherwise show unit-native.
              const hasBothDims = (li.quantity || 0) > 0 && (li.length || 0) > 0;
              const qtyDisplay = hasBothDims
                ? `${li.quantity} × ${(li.length || 0).toFixed(2)} m`
                : (() => {
                    const q = Number(li.quantity || 0);
                    const u = li.unit || '';
                    if (u === 'ea') return `${q} ea`;
                    if (u === 'bin' || u === 'roll') return `${q.toFixed(2)} ${u}`;
                    if (u === 'm²' || u === 'm2') return `${q.toLocaleString(undefined, {maximumFractionDigits: 2})} m²`;
                    if (u === 'm') return `${q.toLocaleString(undefined, {maximumFractionDigits: 2})} m`;
                    return `${q.toLocaleString()} ${u}`.trim();
                  })();
              // v10.29 Sprint 4a · Slice A (2026-02c) — resolved rate
              // display. Backend already emits `type_name` (matched
              // catalogue row name) + `rate` per line. Item column
              // shows the resolved name so the operator sees WHICH
              // catalogue row the spec-driven auto-lookup picked; the
              // new Rate column shows $/unit so a bad match is
              // spottable at a glance (e.g. Barge showing $8/m when it
              // should be $22/m = wrong catalogue row picked).
              return (
              <tr key={i} className="border-t border-slate-100 align-top"
                  data-testid={`reroof-pushed-line-${i}`}
                  data-line-type={li.component_type}>
                <td className="py-1 pr-2 text-slate-700 font-semibold whitespace-nowrap">
                  {li.component_type}
                  {li.ridge_role ? (
                    <span className={`ml-1 px-1 py-0 text-[9px] rounded ${li.ridge_role === 'hip' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`}>{li.ridge_role}</span>
                  ) : null}
                </td>
                <td className="py-1 pr-2 text-slate-700">
                  <div className="truncate max-w-[10rem]" title={li.type_name}
                       data-testid={`reroof-pushed-line-item-${i}`}>
                    {li.type_name || <span className="text-rose-500 italic">— no catalogue match —</span>}
                  </div>
                  {li.uncosted && (
                    <span data-testid={`reroof-line-uncosted-${i}`}
                          className="inline-block mt-0.5 px-1 text-[9px] font-semibold rounded bg-amber-100 text-amber-800 ring-1 ring-amber-300">
                      Uncosted · no catalogue card
                    </span>
                  )}
                  <CalcShadow testid={`reroof-line-calc-${i}`}>
                    {qtyDisplay} × ${(li.rate || 0).toLocaleString()} = <strong>${(li.total || 0).toLocaleString()}</strong>
                  </CalcShadow>
                </td>
                <td className="py-1 pr-2 text-right text-slate-700 whitespace-nowrap">{qtyDisplay}</td>
                <td className="py-1 pr-2 text-right text-slate-600 whitespace-nowrap font-mono-num"
                    data-testid={`reroof-pushed-line-rate-${i}`}>
                  ${(li.rate || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}
                </td>
                <td className="py-1 text-right font-semibold text-slate-900 whitespace-nowrap"
                    data-testid={`reroof-pushed-line-total-${i}`}>
                  ${li.total.toLocaleString()}
                </td>
                <td className="py-1 pl-1 text-center">
                  <button
                    type="button"
                    onClick={() => jumpToComponentsSection(li.component_type)}
                    className="inline-flex items-center justify-center w-5 h-5 rounded text-slate-400 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                    title={`Open the ${li.component_type} section in Components tab`}
                    data-testid={`reroof-pushed-line-jump-${i}`}
                  >
                    ↗
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
    </CalcShadowProvider>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-slate-500 min-w-[110px]">{k}</span>
      <span className="text-slate-800 font-semibold">{v}</span>
    </div>
  );
}



// ────────────────────────────────────────────────────────────────────────
// EmbeddedRoofDiagram — read-only summary tile that expands into the
// full RoofDiagramAnnotator inside a modal when the operator clicks
// "Open editor". Lets the office verify the pins / dimensions feeding
// the Re-roof Builder without leaving this tab.
// ────────────────────────────────────────────────────────────────────────
function EmbeddedRoofDiagram({ api, appraisalId, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [pinCount, setPinCount] = useState(null);
  const [roofProfile, setRoofProfile] = useState(null);
  const [roofType, setRoofType] = useState('');
  const [templateId, setTemplateId] = useState(null);
  const [initialPins, setInitialPins] = useState([]);

  const reloadCount = useCallback(async () => {
    try {
      const r = await api.get(`/appraisals/${appraisalId}`);
      const pins = Array.isArray(r.data?.roof_diagram_annotations) ? r.data.roof_diagram_annotations : [];
      setPinCount(pins.length);
      setInitialPins(pins);
      // Roof profile / type — pulled from the FIRST roof_details entry
      // so the Annotator's Ridge-cap cascade narrows correctly (Iron →
      // RoundTop, Concrete → tile caps). Falls back to the appraisal-
      // level value for older appraisals without roof_details.
      const rd = Array.isArray(r.data?.roof_details) ? r.data.roof_details[0] : null;
      setRoofProfile(rd?.roof_profile || r.data?.roof_profile || null);
      setRoofType(rd?.roof_type || r.data?.roof_type || '');
      setTemplateId(r.data?.roof_design_template_id || r.data?.roof_plan_template_id || null);
    } catch { /* ignore */ }
  }, [api, appraisalId]);

  useEffect(() => { reloadCount(); }, [reloadCount]);

  return (
    <div className="bg-white rounded ring-1 ring-slate-200" data-testid="reroof-embedded-diagram">
      <div className="flex items-center gap-2 px-3 py-2">
        <MapPin size={14} className="text-[#D35400]" />
        <span className="font-semibold text-slate-800 text-sm">Roof Diagram</span>
        <span className="text-xs text-slate-500">
          {pinCount === null ? 'loading…' : (
            pinCount > 0
              ? <>{pinCount} pin{pinCount === 1 ? '' : 's'} on canvas</>
              : <>No pins yet</>
          )}
          {!templateId && <span className="ml-2 text-amber-600">· no template linked</span>}
        </span>
        <button
          type="button"
          data-testid="reroof-embedded-diagram-open"
          onClick={() => setEditing(true)}
          disabled={!templateId}
          title={templateId ? 'Open the diagram editor' : 'Pick a roof template on the appraisal first — Roof Diagram slots card on the Diagrams tab'}
          className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-[#D35400] hover:bg-[#A04000] disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded font-semibold"
        >
          <Edit3 size={12} /> Open editor
        </button>
      </div>
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-stretch justify-stretch p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setEditing(false); }}
          data-testid="reroof-embedded-diagram-modal"
        >
          <div className="bg-white rounded-lg w-full h-full flex flex-col overflow-hidden">
            <div className="flex items-center px-4 py-3 border-b border-slate-200">
              <h3 className="font-bold text-slate-800">Roof Diagram</h3>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  reloadCount();
                  if (onChanged) onChanged();
                }}
                data-testid="reroof-embedded-diagram-close"
                className="ml-auto p-1 hover:bg-slate-100 rounded"
                aria-label="Close diagram editor"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-3">
              {templateId ? (
                <RoofDiagramAnnotator
                  appraisalId={appraisalId}
                  templateId={templateId}
                  initialPins={initialPins}
                  roofProfile={roofProfile}
                  roofType={roofType}
                  slot="roof_plan"
                  onChange={() => {
                    // Refresh the local pin count strip AND bubble up so
                    // the parent Re-roof Builder can re-pull the Live
                    // Dimensions card without waiting for the modal to
                    // close.
                    reloadCount();
                    if (onChanged) onChanged();
                  }}
                />
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-6 text-center">
                  <MapPin size={32} className="mx-auto text-amber-500 mb-2" />
                  <p className="text-sm font-semibold text-amber-900">No roof template linked to this appraisal</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Open the <strong>Diagrams</strong> tab → use the <em>Roof Diagram slots card</em> to pick a base template (e.g. a Google Earth screenshot). Once a template is linked, this embedded editor will load it.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────
// v10.29 Sprint 4a · Slice H (2026-02c) — Miscellaneous Hours section.
// Fixed catalogue rate (3 Misc rows); operator picks the rate_type + fills
// hours + work-type + work-location + optional note. Emitted on Push
// with import_source='reroof_builder_misc_hours' (sticky). No manual rate
// override (per operator ask). No duplicate-last button (also per ask).
// ─────────────────────────────────────────────────────────────────
const MISC_HOURS_RATE_OPTIONS = [
  { value: 'Tradesman Charge Discount p/hr', label: 'Tradesman Charge Discount p/hr' },
  { value: 'Tradesman Charge Out p/hr',      label: 'Tradesman Charge Out p/hr' },
  { value: 'Labourer Charge Out p/hr',       label: 'Labourer Charge Out p/hr' },
];

function MiscHoursSection({ entries, onChange, saving }) {
  const addEntry = () => {
    onChange([
      ...entries,
      {
        rate_type: MISC_HOURS_RATE_OPTIONS[0].value,
        hours: 1,
        work_type: '',
        work_location: '',
        note: '',
      },
    ]);
  };
  const removeEntry = (idx) => {
    if (!window.confirm('Remove this Misc Hours entry? It will be dropped from the next Push.')) return;
    onChange(entries.filter((_, i) => i !== idx));
  };
  const patchEntry = (idx, patch) => {
    onChange(entries.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };

  return (
    <div className="border border-slate-200 rounded bg-slate-50/40 p-3 space-y-2"
         data-testid="reroof-misc-hours-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase font-bold text-slate-600 tracking-wide">
            Miscellaneous Hours
          </div>
          <div className="text-[10px] text-slate-500">
            Extra labour lines · 3 fixed Misc rate cards · sticky across Push
          </div>
        </div>
        <button type="button"
                data-testid="reroof-misc-hours-add"
                onClick={addEntry}
                disabled={saving}
                className="text-[11px] font-bold px-2.5 py-1 rounded border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
          + Add hour entry
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="text-[11px] italic text-slate-400 px-1 py-1.5">
          No entries. Add one to bill extra hourly labour (site meeting, difficult access, cleanup, etc.).
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-slate-200 bg-white">
          <table className="w-full text-xs" data-testid="reroof-misc-hours-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 bg-slate-100">
                <th className="py-1 px-2">Rate</th>
                <th className="py-1 px-2 text-right w-16">Hrs</th>
                <th className="py-1 px-2">Work type</th>
                <th className="py-1 px-2">Location</th>
                <th className="py-1 px-2">Note</th>
                <th className="py-1 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i}
                    className="border-t border-slate-100 align-top"
                    data-testid={`reroof-misc-hours-row-${i}`}>
                  <td className="py-1 px-2">
                    <select value={e.rate_type || MISC_HOURS_RATE_OPTIONS[0].value}
                            data-testid={`reroof-misc-hours-rate-${i}`}
                            onChange={(ev) => patchEntry(i, { rate_type: ev.target.value })}
                            disabled={saving}
                            className="w-full px-1.5 py-0.5 text-xs border border-slate-300 rounded bg-white">
                      {MISC_HOURS_RATE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1 px-2 text-right">
                    <input type="number" step="0.25" min="0"
                           value={e.hours ?? 0}
                           data-testid={`reroof-misc-hours-hrs-${i}`}
                           onChange={(ev) => patchEntry(i, { hours: parseFloat(ev.target.value) || 0 })}
                           disabled={saving}
                           className="w-14 px-1 py-0.5 text-xs border border-slate-300 rounded text-right font-mono-num" />
                  </td>
                  <td className="py-1 px-2">
                    <input type="text"
                           value={e.work_type || ''}
                           data-testid={`reroof-misc-hours-work-type-${i}`}
                           onChange={(ev) => patchEntry(i, { work_type: ev.target.value })}
                           placeholder="e.g. Site meeting"
                           disabled={saving}
                           className="w-full px-1.5 py-0.5 text-xs border border-slate-300 rounded" />
                  </td>
                  <td className="py-1 px-2">
                    <input type="text"
                           value={e.work_location || ''}
                           data-testid={`reroof-misc-hours-location-${i}`}
                           onChange={(ev) => patchEntry(i, { work_location: ev.target.value })}
                           placeholder="e.g. North elevation"
                           disabled={saving}
                           className="w-full px-1.5 py-0.5 text-xs border border-slate-300 rounded" />
                  </td>
                  <td className="py-1 px-2">
                    <input type="text"
                           value={e.note || ''}
                           data-testid={`reroof-misc-hours-note-${i}`}
                           onChange={(ev) => patchEntry(i, { note: ev.target.value })}
                           placeholder="Optional detail"
                           disabled={saving}
                           className="w-full px-1.5 py-0.5 text-xs border border-slate-300 rounded" />
                  </td>
                  <td className="py-1 text-right">
                    <button type="button"
                            data-testid={`reroof-misc-hours-remove-${i}`}
                            onClick={() => removeEntry(i)}
                            disabled={saving}
                            title="Remove entry (confirm)"
                            className="w-6 h-6 text-rose-600 hover:bg-rose-50 rounded">
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

